import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Skeleton } from 'antd';

import InstagramUserInfo from '@/components/RecentFollow/InstagramUserInfo';
import { fetchInsMonitorSearch } from '@/lib/service/home';
import { InsAccountModel } from '@/types/home';

interface InstagramAccountInfoProps {
  mediaName?: string;
  onInfoChange?: (info: InsAccountModel | undefined) => void;
}

const InstagramAccountInfo: React.FC<InstagramAccountInfoProps> = ({ mediaName, onInfoChange }) => {
  const { t } = useTranslation();
  const [innerAccountInfo, setInnerAccountInfo] = useState<InsAccountModel>();
  // CLS 优化：loading 状态控制骨架屏显示
  const [loading, setLoading] = useState(true);

  const initComponentData = useCallback(async () => {
    if (!mediaName) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchInsMonitorSearch(mediaName, undefined);
      setInnerAccountInfo(res.data[0]);
      onInfoChange?.(res.data[0]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [mediaName]);

  useEffect(() => {
    initComponentData();
  }, [initComponentData]);

  // CLS 优化：如果没有 mediaName，不渲染任何内容（包括骨架屏）
  if (!mediaName) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-8 mt-8 max-md:flex-col max-md:gap-4 max-md:mt-5">
      {/* CLS 优化：loading 时显示骨架屏预占位，数据返回后同帧切换 */}
      {loading ? (
        // 骨架屏：头像 + 昵称 - 与 InstagramUserInfo 布局保持一致
        <div className="flex items-center gap-4">
          <Skeleton.Avatar active size={48} shape="circle" />
          <div className="flex items-center gap-3">
            <Skeleton.Input active style={{ width: 120, height: 28 }} />
            <Skeleton.Input active style={{ width: 100, height: 16 }} />
          </div>
        </div>
      ) : (
        innerAccountInfo && (
          <InstagramUserInfo
            currentInsUser={innerAccountInfo}
            hidePrivateMark
            className="mt-0 max-md:items-center"
            avatarClassName="w-12 h-12 max-md:w-12 max-md:h-12"
            fullNameClassName="max-md:text-[1.5rem] max-md:leading-7"
          />
        )
      )}
      <div className="flex items-center gap-8 text-base max-md:text-sm">
        {loading ? (
          // 统计信息骨架屏
          <>
            <Skeleton.Input active style={{ width: 70, height: 20 }} />
            <Skeleton.Input active style={{ width: 100, height: 20 }} />
            <Skeleton.Input active style={{ width: 80, height: 20 }} />
          </>
        ) : (
          <>
            <div className="space-x-1">
              <span className="font-medium">{innerAccountInfo?.media_count}</span>
              <span className="text-[#0C0C0C]/60">{t('Post')}</span>
            </div>
            <div className="space-x-1">
              <span className="font-medium">{innerAccountInfo?.follower_count}</span>
              <span className="text-[#0C0C0C]/60">{t('Followers')}</span>
            </div>
            <div className="space-x-1">
              <span className="font-medium">{innerAccountInfo?.following_count}</span>
              <span className="text-[#0C0C0C]/60">{t('Following')}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstagramAccountInfo;
